<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-10" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>
          <v-alert v-if="success" type="success" class="mb-4" closable @click:close="success = ''">
            {{ success }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else-if="reservation">
            <div class="rd-panel">
              <div class="rd-head">
                <h2 class="rd-head-title">RESERVA DE LA OBRA</h2>
                <p class="rd-head-sub">
                  {{ (reservation.play && reservation.play.title || 'Obra').toUpperCase() }}
                  / FECHA {{ formatDate(reservation.displayDate) }}
                </p>
                <button
                  type="button"
                  class="rd-info-btn"
                  :class="{ 'rd-info-btn-active': selectedKey === 'info' }"
                  @click="selectInfo"
                >
                  INFORMACIÓN DE LA OBRA
                </button>

                <!-- Quién hizo la reserva: lo ven la administración y la compañía
                     en todo el flujo, también al confirmar la fecha. -->
                <div v-if="isManager && clientInfo" class="rd-client">
                  <div class="rd-client-item">
                    <span class="rd-client-label">EMPRESA</span>
                    <span class="rd-client-value">{{ clientInfo.company }}</span>
                  </div>
                  <div class="rd-client-item">
                    <span class="rd-client-label">CONTACTO</span>
                    <span class="rd-client-value">{{ clientInfo.contact }}</span>
                  </div>
                  <div class="rd-client-item">
                    <span class="rd-client-label">CORREO</span>
                    <a v-if="clientInfo.email" class="rd-client-value rd-client-link" :href="`mailto:${clientInfo.email}`">{{ clientInfo.email }}</a>
                    <span v-else class="rd-client-value">—</span>
                  </div>
                  <a
                    v-if="isAdmin && clientInfo.id"
                    class="rd-client-btn"
                    :href="`/clientes/${clientInfo.id}/editar/`"
                    target="_blank"
                    rel="noopener"
                  >
                    <v-icon size="15">mdi-account-box-outline</v-icon>
                    VER PERFIL DEL CLIENTE
                  </a>
                </div>

                <!-- Reserva cancelada: quién, cuándo y por qué. -->
                <div v-if="status === 'cancelled'" class="rd-cancelled">
                  <span class="rd-cancelled-title">
                    <v-icon size="16">mdi-cancel</v-icon>
                    RESERVA CANCELADA
                  </span>
                  <p class="rd-cancelled-line">
                    {{ cancelledSummary }}
                  </p>
                  <p v-if="reservation.cancelReason" class="rd-cancelled-reason">
                    <strong>Motivo:</strong> {{ reservation.cancelReason }}
                  </p>
                </div>

                <!-- Comentario que dejó el cliente al reservar (servicios extra, más
                     funciones…): lo ven la administración y el propio cliente. -->
                <div v-if="(isAdmin || isClient) && reservation.clientComment" class="rd-comment">
                  <span class="rd-client-label">{{ isAdmin ? 'COMENTARIO DEL CLIENTE' : 'TU COMENTARIO (SERVICIOS EXTRA)' }}</span>
                  <p class="rd-comment-text">{{ reservation.clientComment }}</p>
                </div>

                <!-- Admin o compañía, solo mientras esté pendiente de confirmar. -->
                <button
                  v-if="canConfirmDate"
                  type="button"
                  class="rd-confirm-btn"
                  :disabled="sending"
                  @click="confirmRequestedDate"
                >
                  <v-icon size="15">mdi-calendar-check</v-icon>
                  {{ sending ? 'CONFIRMANDO…' : 'CONFIRMAR FECHA' }}
                </button>

                <!-- Rechazo de la fecha: mientras siga en solicitud. -->
                <button
                  v-if="canRejectDate"
                  type="button"
                  class="rd-reject-btn"
                  :disabled="sending"
                  @click="rejectDate"
                >
                  <v-icon size="15">mdi-calendar-remove</v-icon>
                  {{ sending ? 'RECHAZANDO…' : 'RECHAZAR FECHA' }}
                </button>

                <!-- Cancelar la reserva (con motivo): la administración siempre; el
                     cliente y la compañía hasta la firma de contratos. -->
                <button
                  v-if="canCancel"
                  type="button"
                  class="rd-cancel-btn"
                  :disabled="sending"
                  @click="openCancel('cancel')"
                >
                  <v-icon size="15">mdi-close-circle-outline</v-icon>
                  CANCELAR RESERVA
                </button>
              </div>

              <div class="rd-view">
                <!-- Estados: el más reciente arriba; los futuros no se muestran. -->
                <div class="rd-left">
                  <div class="rd-timeline">
                    <div
                      v-for="s in states"
                      :key="s.key"
                      class="rd-state"
                      :class="{ 'rd-state-selected': selectedKey === s.key }"
                      @click="select(s.key)"
                    >
                      <span
                        class="rd-state-dot"
                        :class="`rd-dot-${s.state}`"
                        :style="dotStyle(s)"
                      ></span>
                      <div class="rd-state-body">
                        <div class="rd-state-title">
                          {{ s.title }}
                          <span v-if="s.needsAction" class="rd-action-badge">ACCIÓN</span>
                        </div>
                        <div v-if="s.subtitle" class="rd-state-subtitle">
                          {{ s.subtitle }}
                        </div>
                        <div
                          v-for="(line, i) in s.lines"
                          :key="i"
                          class="rd-state-line"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Tramo administración <-> compañía de teatro: corre en paralelo
                       al cobro al cliente y éste no lo ve. -->
                  <div v-if="showCompanyTrack" class="rd-track">
                    <p class="rd-track-title">PAGOS A LA COMPAÑÍA DE TEATRO</p>
                    <p class="rd-track-sub">SÓLO LO VEN LA ADMINISTRACIÓN Y LA COMPAÑÍA</p>
                    <div class="rd-timeline">
                      <div
                        v-for="t in trackItems"
                        :key="t.key"
                        class="rd-state"
                        :class="{ 'rd-state-selected': selectedKey === t.key, 'rd-state-locked': !t.open }"
                        @click="select(t.key)"
                      >
                        <span
                          class="rd-state-dot"
                          :class="`rd-dot-${t.state}`"
                          :style="dotStyle(t)"
                        ></span>
                        <div class="rd-state-body">
                          <div class="rd-state-title">
                            {{ t.title }}
                            <span v-if="t.needsAction" class="rd-action-badge">ACCIÓN</span>
                          </div>
                          <div v-if="t.subtitle" class="rd-state-subtitle">
                            {{ t.subtitle }}
                          </div>
                          <div v-for="(line, i) in t.lines" :key="i" class="rd-state-line">
                            {{ line }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Documento / información del estado seleccionado. -->
                <div class="rd-right">
                  <div class="rd-doc-card">
                    <!-- RESUMEN DE LA OBRA -->
                    <template v-if="showFicha">
                      <h3 class="rd-doc-title">{{ (play && play.title || reservation.play && reservation.play.title || '').toUpperCase() }}</h3>
                      <p class="rd-doc-desc">{{ (play && (play.synopsis || play.description)) || '' }}</p>

                      <div class="rd-facts">
                        <div v-for="fact in playFacts" :key="fact.label" class="rd-fact">
                          <span class="rd-fact-label">{{ fact.label }}</span>
                          <span class="rd-fact-value">{{ fact.value }}</span>
                        </div>
                        <div class="rd-fact">
                          <span class="rd-fact-label">VALOR</span>
                          <!-- El total de la reserva (obra + servicios extra), no el precio de lista. -->
                          <span class="rd-fact-value">{{ reservation.totalAmount ? formatCLP(reservation.totalAmount) : ((play && play.price) || formatCLP(0)) }}</span>
                        </div>
                        <div v-if="hasRegionPrice" class="rd-fact">
                          <span class="rd-fact-label">VALOR REGIÓN</span>
                          <span class="rd-fact-value">{{ play.priceRegionDisplay }}</span>
                        </div>
                      </div>

                      <p class="rd-doc-foot">
                        * Los ítems transporte, alojamiento y alimentación serán
                        responsabilidad del contratante, siempre que la obra se
                        presente a una distancia mayor a 160 km de Santiago.
                      </p>
                    </template>

                    <!-- DOCUMENTO / INFORMACIÓN DEL ESTADO -->
                    <template v-if="!showFicha">
                      <h3 class="rd-doc-title">{{ selectedState ? selectedState.title : '' }}</h3>

                      <!-- Firma de contratos: la administración arma los precios y
                           los dos contratos (cliente y compañía) y los envía; cada
                           parte ve y acepta el suyo recién cuando se lo envían. -->
                      <div v-if="selectedState && selectedState.contract" class="rd-contract">
                        <!-- Precios: obra + servicios extra. Editables hasta enviar. -->
                        <div v-if="canEditContracts && pricing" class="rd-pricing">
                          <p class="rd-pricing-title">PRECIOS</p>
                          <!-- Precios de lista de la obra, como referencia para fijar el
                               de la reserva (fuera de la Región Metropolitana rige el
                               precio región). -->
                          <div class="rd-pricing-ref">
                            <span>
                              Precio de lista de la obra: <strong>{{ formatCLP(pricing.play_price) }}</strong>
                              <button v-if="pricing.editable" type="button" class="rd-pricing-use" :disabled="sending" @click="usePlayPrice(pricing.play_price)">USAR</button>
                            </span>
                            <span v-if="pricing.play_price_region">
                              Precio fuera de la región: <strong>{{ formatCLP(pricing.play_price_region) }}</strong>
                              <button v-if="pricing.editable" type="button" class="rd-pricing-use" :disabled="sending" @click="usePlayPrice(pricing.play_price_region)">USAR</button>
                            </span>
                            <span v-else class="rd-pricing-ref-muted">La compañía no definió un precio fuera de la región.</span>
                          </div>
                          <p v-if="reservation.clientComment" class="rd-pricing-comment">
                            <strong>Comentario del cliente:</strong> {{ reservation.clientComment }}
                          </p>
                          <template v-if="pricing.editable">
                            <div class="rd-pricing-row">
                              <span class="rd-pricing-name rd-pricing-fixed">Fecha de la función</span>
                              <input
                                v-model="pricingDraft.function_date"
                                class="rd-pricing-input rd-pricing-amount rd-pricing-date"
                                type="date"
                                :min="playSeasonStart"
                                :max="playSeasonEnd"
                                :disabled="sending"
                              />
                              <span class="rd-pricing-spacer"></span>
                            </div>
                            <div class="rd-pricing-row">
                              <span class="rd-pricing-name rd-pricing-fixed">
                                Precio de la obra «{{ (reservation.play && reservation.play.title) || '' }}»
                              </span>
                              <input
                                :value="formatThousands(pricingDraft.play_amount)"
                                class="rd-pricing-input rd-pricing-amount"
                                type="text"
                                inputmode="numeric"
                                placeholder="0"
                                :disabled="sending"
                                @input="setAmount(pricingDraft, 'play_amount', $event)"
                              />
                              <span class="rd-pricing-spacer"></span>
                            </div>
                            <div v-for="(service, i) in pricingDraft.services" :key="i" class="rd-pricing-row">
                              <input
                                v-model="service.name"
                                class="rd-pricing-input rd-pricing-name"
                                type="text"
                                maxlength="200"
                                placeholder="Nombre del servicio"
                                :disabled="sending"
                              />
                              <input
                                :value="formatThousands(service.amount)"
                                class="rd-pricing-input rd-pricing-amount"
                                type="text"
                                inputmode="numeric"
                                placeholder="0"
                                :disabled="sending"
                                @input="setAmount(service, 'amount', $event)"
                              />
                              <button type="button" class="rd-pricing-remove" title="Quitar servicio" :disabled="sending" @click="removeService(i)">
                                <v-icon size="18">mdi-close</v-icon>
                              </button>
                            </div>
                            <button type="button" class="rd-secondary-btn rd-pricing-add" :disabled="sending" @click="addService">
                              <v-icon size="15">mdi-plus</v-icon>
                              AGREGAR SERVICIO
                            </button>
                            <div class="rd-pricing-row rd-pricing-total">
                              <span class="rd-pricing-name">TOTAL</span>
                              <strong class="rd-pricing-amount">{{ formatCLP(pricingDraftTotal) }}</strong>
                              <span class="rd-pricing-spacer"></span>
                            </div>
                            <p class="rd-pricing-hint">
                              Reserva (20%): {{ formatCLP(Math.floor(pricingDraftTotal * 20 / 100)) }} ·
                              Saldo: {{ formatCLP(pricingDraftTotal - Math.floor(pricingDraftTotal * 20 / 100)) }}.
                              La fecha y los precios quedan fijos al enviar los contratos.
                            </p>
                            <button type="button" class="rd-secondary-btn" :disabled="sending || !pricingDirty" @click="savePricing">
                              <v-icon size="15">mdi-content-save</v-icon>
                              GUARDAR FECHA Y PRECIOS
                            </button>
                          </template>
                          <template v-else>
                            <div class="rd-pricing-row">
                              <span class="rd-pricing-name">Fecha de la función</span>
                              <span class="rd-pricing-amount">{{ formatDate(pricing.function_date) }}</span>
                            </div>
                            <div class="rd-pricing-row">
                              <span class="rd-pricing-name">Precio de la obra «{{ (reservation.play && reservation.play.title) || '' }}»</span>
                              <span class="rd-pricing-amount">{{ formatCLP(pricing.play_amount) }}</span>
                            </div>
                            <div v-for="service in pricing.services" :key="service.id" class="rd-pricing-row">
                              <span class="rd-pricing-name">{{ service.name }}</span>
                              <span class="rd-pricing-amount">{{ formatCLP(service.amount) }}</span>
                            </div>
                            <div class="rd-pricing-row rd-pricing-total">
                              <span class="rd-pricing-name">TOTAL</span>
                              <strong class="rd-pricing-amount">{{ formatCLP(pricing.total_amount) }}</strong>
                            </div>
                            <p class="rd-pricing-hint">La fecha y los precios quedaron fijos al enviar los contratos.</p>
                          </template>
                        </div>

                        <!-- La administración ve los dos contratos, uno por pestaña. -->
                        <div v-if="isAdmin && contracts.cliente && contracts.cia" class="rd-contract-tabs">
                          <button
                            v-for="tab in contractTabs"
                            :key="tab.kind"
                            type="button"
                            class="rd-contract-tab"
                            :class="{ 'rd-contract-tab-active': contractTab === tab.kind }"
                            @click="contractTab = tab.kind"
                          >
                            {{ tab.label }}
                            <v-icon v-if="contracts[tab.kind].signed_at" size="15" color="#2ea357">mdi-check-circle</v-icon>
                          </button>
                        </div>

                        <template v-if="canEditContracts">
                          <p class="rd-contract-status">
                            <template v-if="activeContract.sent_at">
                              <v-icon size="15" color="#2ea357">mdi-email-check</v-icon>
                              Enviado a {{ activeContract.recipient }} el {{ formatDate(activeContract.sent_at) }}.
                              Puedes editar los contratos y reenviarlos mientras nadie los acepte.
                            </template>
                            <template v-else>
                              <v-icon size="15" color="#c8102e">mdi-pencil</v-icon>
                              Revisa los precios y edita los dos contratos. Nadie los recibe hasta que pongas «Enviar contratos».
                            </template>
                          </p>
                          <contract-editor :key="contractTab" v-model="drafts[contractTab]" :disabled="sending" />
                          <!-- El anexo de precios se agrega solo al final de cada contrato. -->
                          <div v-if="contracts.annex_html" class="rd-contract-body rd-contract-html rd-contract-annex">
                            <p class="rd-contract-annex-note">
                              <v-icon size="14">mdi-lock-outline</v-icon>
                              Anexo automático (con los precios guardados): se agrega al final de ambos contratos.
                            </p>
                            <div v-html="contracts.annex_html"></div>
                          </div>
                          <p class="rd-contract-recipient">
                            <v-icon size="15">mdi-email-outline</v-icon>
                            Este contrato se enviará a:
                            <strong v-if="activeContract.recipient">{{ activeContract.recipient }}</strong>
                            <span v-else class="rd-contract-recipient-missing">
                              {{ contractTab === 'cia' ? 'la compañía no tiene correo de contacto' : 'la reserva no tiene correo' }}
                            </span>
                            <span v-if="contractTab === 'cliente' && reservation.name" class="rd-contract-recipient-name">({{ reservation.name }})</span>
                            <span v-if="contractTab === 'cia'" class="rd-contract-recipient-name">({{ companyName }})</span>
                          </p>
                          <div class="rd-contract-actions rd-contract-actions-wrap">
                            <button type="button" class="rd-accept-btn" :disabled="sending" @click="sendAllContracts">
                              <v-icon size="15">mdi-send</v-icon>
                              {{ sending ? 'ENVIANDO…' : (reservation.contractSentAt ? 'REENVIAR CONTRATOS' : 'ENVIAR CONTRATOS') }}
                            </button>
                            <button type="button" class="rd-secondary-btn" :disabled="sending" @click="saveContractDraft">
                              <v-icon size="15">mdi-content-save</v-icon>
                              GUARDAR BORRADOR
                            </button>
                            <button type="button" class="rd-secondary-btn" :disabled="sending" @click="resetContractDraft">
                              <v-icon size="15">mdi-restore</v-icon>
                              RESTAURAR TEXTO ORIGINAL
                            </button>
                            <button type="button" class="rd-secondary-btn" :disabled="sending || generatingPdf" @click="downloadActiveContractPdf">
                              <v-icon size="15">mdi-file-download</v-icon>
                              {{ generatingPdf ? 'GENERANDO PDF…' : 'GENERAR Y DESCARGAR PDF' }}
                            </button>
                            <a v-if="activeContract.pdf_url" class="rd-secondary-btn" :href="activeContract.pdf_url" target="_blank" rel="noopener">
                              <v-icon size="15">mdi-file-pdf-box</v-icon>
                              VER PDF ENVIADO
                            </a>
                          </div>
                        </template>

                        <template v-else-if="activeContract.sent_html">
                          <div class="rd-contract-body rd-contract-html" v-html="activeContract.sent_html"></div>

                          <!-- Aceptación de la contraparte: identidad, código al correo y casilla. -->
                          <div v-if="canAcceptActive && !isAdmin && signer" class="rd-accept-box">
                            <p class="rd-accept-title">ACEPTAR EL CONTRATO</p>
                            <p class="rd-accept-line">
                              Aceptas como <strong>{{ signer.name }}</strong>, en representación de
                              <strong>{{ signer.company }}</strong>, RUT <strong>{{ signer.rut }}</strong>.
                            </p>
                            <p class="rd-accept-line">
                              1. Enviaremos un código de 6 dígitos a <strong>{{ signer.email }}</strong>
                              <template v-if="contractKind === 'cia'">, el correo de la compañía,</template>
                              (vence en {{ activeContract.code_valid_minutes }} minutos).
                            </p>
                            <button type="button" class="rd-secondary-btn mb-3" :disabled="sendingCode || sending" @click="requestCode">
                              <v-icon size="15">mdi-email-fast</v-icon>
                              {{ sendingCode ? 'ENVIANDO…' : (codeSent ? 'REENVIAR CÓDIGO' : 'ENVIAR CÓDIGO') }}
                            </button>
                            <p v-if="codeSent" class="rd-accept-hint">
                              Enviamos el código a {{ signer.email }}. Revisa el correo (y la carpeta de spam).
                            </p>
                            <p class="rd-accept-line">2. Ingresa el código y confirma:</p>
                            <v-text-field
                              v-model="acceptCode"
                              label="Código de 6 dígitos"
                              variant="outlined"
                              density="comfortable"
                              maxlength="6"
                              inputmode="numeric"
                              autocomplete="one-time-code"
                              class="rd-code-input"
                            />
                            <v-checkbox
                              v-model="acceptChecked"
                              density="compact"
                              hide-details
                              label="He leído el contrato y acepto sus términos."
                            />
                            <v-alert
                              v-if="acceptError"
                              type="error"
                              density="compact"
                              variant="tonal"
                              class="mt-3"
                            >
                              {{ acceptError }}
                            </v-alert>
                            <button
                              type="button"
                              class="rd-accept-btn mt-3"
                              :disabled="sending || !acceptChecked || acceptCodeDigits.length !== 6"
                              @click="acceptActiveContract"
                            >
                              <v-icon size="15">mdi-check-decagram</v-icon>
                              {{ sending ? 'ACEPTANDO…' : 'ESTOY DE ACUERDO Y ACEPTO EL CONTRATO' }}
                            </button>
                            <div class="rd-reject-row">
                              <button type="button" class="rd-reject-link" :disabled="sending" @click="openCancel('reject')">
                                <v-icon size="15">mdi-close-circle-outline</v-icon>
                                NO ESTOY DE ACUERDO: RECHAZAR EL CONTRATO
                              </button>
                              <span class="rd-reject-hint">Al rechazarlo la reserva se cancela; te pediremos el motivo.</span>
                            </div>
                          </div>

                          <div class="rd-contract-actions">
                            <span v-if="activeContract.signed_at" class="rd-contract-signed">
                              <v-icon size="16" color="#2ea357">mdi-check-circle</v-icon>
                              Contrato aceptado
                            </span>
                            <span v-if="otherPartyPending" class="rd-contract-waiting">
                              {{ otherPartyPending }}
                            </span>
                            <a v-if="activeContract.pdf_url" class="rd-secondary-btn" :href="activeContract.pdf_url" target="_blank" rel="noopener">
                              <v-icon size="15">mdi-file-pdf-box</v-icon>
                              DESCARGAR PDF
                            </a>
                          </div>
                        </template>

                        <p v-else class="rd-doc-empty">
                          El contrato está en preparación. Te llegará por correo cuando esté listo para revisarlo.
                        </p>

                        <!-- Estado del correo y reenvío (administración). -->
                        <div v-if="isAdmin && activeContract.sent_at" class="rd-email-status">
                          <p v-if="activeContract.email_error" class="rd-email-error">
                            <v-icon size="16" color="#c8102e">mdi-email-alert</v-icon>
                            No se pudo enviar el correo a {{ activeContract.recipient }}: {{ activeContract.email_error }}
                          </p>
                          <p v-else-if="activeContract.email_sent_at" class="rd-email-ok">
                            <v-icon size="16" color="#2ea357">mdi-email-check</v-icon>
                            Correo enviado a {{ activeContract.recipient }} el {{ formatDateTime(activeContract.email_sent_at) }}.
                          </p>
                          <button
                            v-if="canResendEmail"
                            type="button"
                            class="rd-secondary-btn"
                            :disabled="sending || resendingEmail"
                            @click="resendEmail"
                          >
                            <v-icon size="15">mdi-email-sync</v-icon>
                            {{ resendingEmail ? 'REENVIANDO…' : 'REENVIAR CORREO' }}
                          </button>
                        </div>

                        <!-- La administración puede aceptar cualquiera de los dos
                             contratos en nombre de su contraparte. -->
                        <div v-if="adminAcceptKinds.length" class="rd-contract-actions rd-contract-actions-wrap">
                          <button
                            v-for="kind in adminAcceptKinds"
                            :key="kind"
                            type="button"
                            class="rd-accept-btn"
                            :disabled="sending"
                            @click="acceptContractAsAdmin(kind)"
                          >
                            <v-icon size="15">mdi-check-decagram</v-icon>
                            ACEPTAR EN NOMBRE {{ kind === 'cia' ? 'DE LA COMPAÑÍA' : 'DEL CLIENTE' }}
                          </button>
                        </div>
                      </div>

                      <!-- Estreno de la función: realizado o en espera. -->
                      <div v-else-if="selectedKey === 'balance_pending' && showPremiered" class="rd-doc-info">
                        <p class="rd-doc-info-line">
                          Estreno realizado<template v-if="showDate"> el {{ formatDate(showDate) }}</template>.
                        </p>
                        <p class="rd-doc-empty">El cliente ya puede subir el comprobante del saldo.</p>
                      </div>
                      <div v-else-if="selectedKey === 'balance_pending'" class="rd-doc-info">
                        <p class="rd-doc-info-line">
                          A la espera del estreno de la función<template v-if="reservation.confirmedDate || reservation.requestedDate"> el {{ formatDate(reservation.confirmedDate || reservation.requestedDate) }}</template>.
                        </p>
                        <p class="rd-doc-empty">Cuando pase la fecha del estreno, el cliente podrá subir el comprobante del saldo.</p>
                      </div>

                      <!-- Paso con documento (comprobante / factura): subir, o ver + volver a subir. -->
                      <template v-else-if="selectedIsDocStep">
                        <template v-if="selectedDocUrl">
                          <div class="rd-doc-viewer">
                            <iframe v-if="isPdf(selectedDocUrl)" :src="selectedDocUrl" class="rd-doc-frame" title="Documento"></iframe>
                            <img v-else-if="isImage(selectedDocUrl)" :src="selectedDocUrl" class="rd-doc-img" alt="Documento" />
                            <a
                              class="rd-doc-open"
                              :style="docButtonStyle"
                              :href="selectedDocUrl"
                              target="_blank"
                              rel="noopener"
                            >
                              <v-icon size="16">mdi-open-in-new</v-icon>
                              {{ (selectedState && selectedState.docLabel) || 'ABRIR DOCUMENTO' }}
                            </a>
                          </div>
                          <div v-if="selectedCanManage && status !== 'cancelled'" class="rd-reupload">
                            <v-file-input v-model="uploadFile" :label="selectedState.reuploadLabel" accept="image/*,.pdf" variant="outlined" density="comfortable" class="mb-2" />
                            <v-btn color="red-darken-3" :loading="sending" @click="doUpload">{{ selectedState.reuploadLabel }}</v-btn>
                          </div>
                        </template>
                        <div v-else-if="selectedUploadEnabled" class="rd-action">
                          <p v-if="selectedState.amount === 'deposit'" class="mb-2">Debes pagar el 20% de la reserva: <strong>{{ formatCLP(reservation.depositAmount) }}</strong></p>
                          <p v-else-if="selectedState.amount === 'balance'" class="mb-2">Debes pagar el saldo (80%): <strong>{{ formatCLP(reservation.balanceAmount) }}</strong></p>
                          <p v-else-if="selectedState.amount === 'company_advance'" class="mb-2">Anticipo a la compañía (10% del total): <strong>{{ formatCLP(reservation.companyAdvanceAmount) }}</strong></p>
                          <p v-else-if="selectedState.amount === 'company_balance'" class="mb-2">Saldo a la compañía (80% del total): <strong>{{ formatCLP(reservation.companyBalanceAmount) }}</strong></p>
                          <v-file-input v-model="uploadFile" :label="selectedState.uploadLabel" accept="image/*,.pdf" variant="outlined" density="comfortable" class="mb-3" />
                          <v-textarea v-model="notes" label="Notas (opcional)" rows="2" variant="outlined" class="mb-3" />
                          <v-btn color="red-darken-3" :loading="sending" @click="doUpload">{{ selectedState.uploadLabel }}</v-btn>
                        </div>
                        <div v-else class="rd-doc-info">
                          <p v-if="selectedState.amount === 'company_advance'" class="rd-doc-info-line">Anticipo a la compañía (10% del total): {{ formatCLP(reservation.companyAdvanceAmount) }}</p>
                          <p v-else-if="selectedState.amount === 'company_balance'" class="rd-doc-info-line">Saldo a la compañía (80% del total): {{ formatCLP(reservation.companyBalanceAmount) }}</p>
                          <p class="rd-doc-empty">
                            <template v-if="selectedState.track && !trackOpen(selectedState)">{{ selectedState.lockedHint }}</template>
                            <template v-else>Aún no se ha subido este documento.</template>
                          </p>
                        </div>
                      </template>

                      <!-- Información del estado. -->
                      <div v-else class="rd-doc-info">
                        <p v-for="(line, i) in selectedStateLines" :key="i" class="rd-doc-info-line">
                          {{ line }}
                        </p>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="rd-volver-row">
              <button type="button" class="rd-volver" @click="goBack">
                <v-icon size="18">mdi-undo</v-icon>
                VOLVER
              </button>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirmación antes de enviar los dos contratos. -->
    <transition name="video-fade">
      <div v-if="sendConfirmOpen" class="video-overlay" @click.self="closeSendConfirm">
        <div class="contact-dialog rd-confirm-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" :disabled="sending" @click="closeSendConfirm">✕</button>
          <h3 class="contact-title">{{ reservation.contractSentAt ? 'REENVIAR CONTRATOS' : 'ENVIAR CONTRATOS' }}</h3>
          <p class="rd-confirm-text">
            Se generarán los PDFs con el anexo de precios y cada parte recibirá el suyo
            por correo, con el enlace para aceptarlo.
          </p>
          <ul class="rd-confirm-list">
            <li>
              <span class="rd-confirm-label">CONTRATO CLIENTE</span>
              <span class="rd-confirm-value">{{ contracts.cliente && contracts.cliente.recipient }}</span>
              <span v-if="reservation.name" class="rd-confirm-hint">{{ reservation.name }}</span>
            </li>
            <li>
              <span class="rd-confirm-label">CONTRATO COMPAÑÍA</span>
              <span class="rd-confirm-value">{{ contracts.cia && contracts.cia.recipient }}</span>
              <span class="rd-confirm-hint">{{ companyName }}</span>
            </li>
            <li>
              <span class="rd-confirm-label">TOTAL</span>
              <span class="rd-confirm-value">{{ formatCLP(pricingDraftTotal) }}</span>
              <span class="rd-confirm-hint">Reserva (20%): {{ formatCLP(Math.floor(pricingDraftTotal * 20 / 100)) }}</span>
            </li>
          </ul>
          <p v-if="!reservation.contractSentAt" class="rd-confirm-warning">
            <v-icon size="15">mdi-lock-outline</v-icon>
            Después de enviarlos ya no se pueden cambiar los precios.
          </p>
          <p v-if="sendError" class="rd-confirm-error">{{ sendError }}</p>
          <div class="rd-confirm-actions">
            <button type="button" class="rd-confirm-cancel" :disabled="sending" @click="closeSendConfirm">CANCELAR</button>
            <v-btn class="rd-confirm-submit" color="red-darken-3" rounded="lg" :loading="sending" @click="confirmSendContracts">
              {{ reservation.contractSentAt ? 'REENVIAR' : 'ENVIAR' }}
            </v-btn>
          </div>
        </div>
      </div>
    </transition>

    <!-- Cancelar la reserva (o rechazar el contrato): motivo obligatorio. -->
    <transition name="video-fade">
      <div v-if="cancelOpen" class="video-overlay" @click.self="closeCancel">
        <div class="contact-dialog rd-confirm-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" :disabled="sending" @click="closeCancel">✕</button>
          <h3 class="contact-title">{{ cancelMode === 'reject' ? 'RECHAZAR EL CONTRATO' : 'CANCELAR LA RESERVA' }}</h3>
          <p class="rd-confirm-text">
            <template v-if="cancelMode === 'reject'">
              Al rechazar el contrato la reserva queda cancelada y la fecha vuelve a estar disponible.
            </template>
            <template v-else>
              La reserva queda cancelada y la fecha vuelve a estar disponible.
            </template>
            Se avisará por correo al cliente, a la compañía y a la administración.
          </p>
          <label class="rd-cancel-label" for="rd-cancel-reason">Motivo (obligatorio)</label>
          <textarea
            id="rd-cancel-reason"
            v-model="cancelReason"
            class="rd-cancel-input"
            rows="4"
            maxlength="2000"
            :disabled="sending"
            :placeholder="cancelMode === 'reject' ? 'Cuéntanos qué parte del contrato no te acomoda…' : '¿Por qué se cancela la reserva?'"
          ></textarea>
          <p v-if="cancelError" class="rd-confirm-error">{{ cancelError }}</p>
          <div class="rd-confirm-actions">
            <button type="button" class="rd-confirm-cancel" :disabled="sending" @click="closeCancel">VOLVER</button>
            <v-btn class="rd-confirm-submit" color="red-darken-3" rounded="lg" :loading="sending" :disabled="!cancelReason.trim()" @click="confirmCancel">
              {{ cancelMode === 'reject' ? 'RECHAZAR Y CANCELAR' : 'CANCELAR RESERVA' }}
            </v-btn>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirmación antes de restaurar el texto original de un contrato. -->
    <transition name="video-fade">
      <div v-if="resetConfirmOpen" class="video-overlay" @click.self="closeResetConfirm">
        <div class="contact-dialog rd-confirm-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" :disabled="sending" @click="closeResetConfirm">✕</button>
          <h3 class="contact-title">RESTAURAR TEXTO ORIGINAL</h3>
          <p class="rd-confirm-text">
            El {{ contractTab === 'cia' ? 'contrato de la compañía' : 'contrato del cliente' }} vuelve al
            texto original, rellenado con los datos de la reserva.
          </p>
          <p class="rd-confirm-warning">
            <v-icon size="15">mdi-alert-outline</v-icon>
            Se pierden los cambios hechos en el editor de este contrato. El otro contrato no se modifica.
          </p>
          <p v-if="resetError" class="rd-confirm-error">{{ resetError }}</p>
          <div class="rd-confirm-actions">
            <button type="button" class="rd-confirm-cancel" :disabled="sending" @click="closeResetConfirm">CANCELAR</button>
            <v-btn class="rd-confirm-submit" color="red-darken-3" rounded="lg" :loading="sending" @click="confirmResetContract">
              RESTAURAR
            </v-btn>
          </div>
        </div>
      </div>
    </transition>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchReservation,
  fetchPlay,
  fetchContracts,
  confirmReservationDate,
  rejectReservationDate,
  acceptContract,
  saveContract,
  saveReservationPricing,
  sendContracts,
  resendContractEmail,
  downloadContractPdf,
  requestAcceptanceCode,
  uploadDepositProof,
  uploadReservationInvoice,
  generateBalanceInvoice,
  uploadBalanceProof,
  uploadCompanyInvoice,
  uploadCompanyPayment,
  uploadCompanyAdvancePayment,
  uploadCompanyAdvanceInvoice,
  cancelReservation,
} from "@/services/teatroApi";
import { TIMELINE_COLORS as C, FLOW_ORDER } from "./reservationStatus";
import ContractEditor from "./ContractEditor.vue";

// Funciones de subida por paso (subir = primera vez avanza; re-subir reemplaza).
const UPLOADERS = {
  uploadDepositProof,
  uploadReservationInvoice,
  generateBalanceInvoice,
  uploadBalanceProof,
  uploadCompanyInvoice,
  uploadCompanyPayment,
  uploadCompanyAdvancePayment,
  uploadCompanyAdvanceInvoice,
};

// Estados en los que el cliente y la compañía pueden cancelar por su cuenta
// (Reservation.SELF_CANCELLABLE del backend). Después, sólo la administración.
const SELF_CANCELLABLE = [
  "pending",
  "pending_availability",
  "date_requested",
  "date_confirmed",
  "contract_pending",
];

// Estados de la reserva, en orden del flujo (Reservation.Status del backend).
// Se muestran en la vista en orden INVERSO (el más reciente arriba).
//  - showsInfo: al seleccionarlo, a la derecha se muestra el resumen de la obra.
//  - subtitle: entre quiénes ocurre el hito ("DESDE LA ADMIN. AL COMPRADOR", …).
//  - docField / docLabel: documento asociado y etiqueta del botón "VER …".
//  - color / fill: color del punto según el mockup de la línea de tiempo.
//    Verde = acuerdo y firmas · azul = admin ↔ comprador ·
//    morado = estreno · magenta = admin ↔ compañía de teatro.
const STEPS = [
  {
    key: "pending_availability", title: "DISPONIBILIDAD DE FECHA", showsInfo: true,
    color: C.available, fill: false,
  },
  {
    key: "date_requested", title: "SOLICITUD DE FECHA", showsInfo: true,
    color: C.agreement, fill: false,
  },
  {
    key: "date_confirmed", title: "CONFIRMACIÓN DE FECHA",
    color: C.agreement, fill: false,
  },
  {
    // Los dos contratos: el del cliente comprador y el de la compañía de
    // teatro. Cada parte ve el suyo; la administración, ambos.
    key: "contract_pending", title: "FIRMA DE CONTRATOS",
    contract: true,
    color: C.agreement, fill: true,
  },
  {
    key: "deposit_pending", title: "PAGO DE LA RESERVA (20%)",
    subtitle: "DESDE EL COMPRADOR A LA ADMIN.",
    docField: "depositProofUrl", docLabel: "VER COMPROBANTE",
    role: "owner", uploadFn: "uploadDepositProof", amount: "deposit",
    uploadLabel: "Subir comprobante", reuploadLabel: "Volver a subir comprobante",
    // En el mockup el pago del 20% va junto a la firma del cliente (verde).
    color: C.agreement, fill: true,
  },
  {
    key: "deposit_paid", title: "FACTURA DE LA RESERVA (20%)",
    subtitle: "DESDE LA ADMIN. AL COMPRADOR",
    docField: "reservationInvoiceUrl", docLabel: "VER FACTURA",
    // Documento de la administración al comprador: la compañía no lo emite.
    role: "admin", uploadFn: "uploadReservationInvoice",
    uploadLabel: "Subir factura del 20%", reuploadLabel: "Volver a subir factura",
    color: C.client, fill: false,
  },
  {
    key: "balance_invoice_pending", title: "FACTURA POR EL SALDO (80%)",
    subtitle: "DESDE LA ADMIN. AL COMPRADOR",
    docField: "balanceInvoiceUrl", docLabel: "VER FACTURA POR EL SALDO",
    // Ídem: la factura de la compañía es la del hito magenta, no ésta.
    role: "admin", uploadFn: "generateBalanceInvoice",
    uploadLabel: "Subir factura del saldo", reuploadLabel: "Volver a subir factura",
    color: C.client, fill: true,
  },
  {
    key: "balance_pending", title: "ESTRENO DE FUNCIÓN", waiting: true,
    color: C.premiere, fill: true,
  },
  {
    key: "show_completed", title: "PAGO DEL SALDO (80%)",
    subtitle: "DESDE EL CLIENTE A LA ADMINISTRACIÓN",
    docField: "balanceProofUrl", docLabel: "BOUCHER PAGO DEL SALDO",
    role: "owner", uploadFn: "uploadBalanceProof", amount: "balance",
    uploadLabel: "Subir comprobante", reuploadLabel: "Volver a subir comprobante",
    color: C.client, fill: true,
  },
  {
    key: "balance_paid", title: "PAGO DEL SALDO RECIBIDO",
    subtitle: "DESDE EL CLIENTE A LA ADMINISTRACIÓN",
    color: C.client, fill: true,
  },
  // --- Cierre: el tramo con la compañía (ver COMPANY_TRACK) terminó. El
  // cliente no ve este paso: para él la reserva termina con el saldo pagado.
  {
    key: "company_paid", title: "RESERVA CERRADA",
    subtitle: "SALDO TRANSFERIDO A LA CÍA. Y FACTURADO",
    managerOnly: true,
    color: C.company, fill: true,
  },
];

// Tramo administración <-> compañía de teatro (magenta en el mockup). Corre en
// paralelo al cobro al cliente, que no lo ve: con el 20% del cliente pagado la
// administración transfiere el anticipo (10%) y la compañía lo factura; con el
// 80% pagado, lo mismo con el saldo (80%).
//  - opensAt: estado del flujo principal desde el que se habilita.
//  - requires: campo de la reserva que debe existir antes (la transferencia).
//  - dateField: cuándo se subió el documento.
const COMPANY_TRACK = [
  {
    key: "company_advance_proof", track: true,
    title: "ANTICIPO A LA COMPAÑÍA (10%)",
    subtitle: "DESDE LA ADMIN. A LA CÍA. DE TEATRO",
    docField: "companyAdvanceProofUrl", docLabel: "BOUCHER DEL ANTICIPO",
    dateField: "companyAdvancePaidAt",
    role: "admin", uploadFn: "uploadCompanyAdvancePayment", amount: "company_advance",
    uploadLabel: "Subir comprobante del anticipo", reuploadLabel: "Volver a subir comprobante",
    opensAt: "deposit_paid",
    lockedHint: "Se habilita cuando el cliente pague la reserva (20%).",
    color: C.company, fill: false,
  },
  {
    key: "company_advance_invoice", track: true,
    title: "FACTURA POR EL ANTICIPO (10%)",
    subtitle: "DESDE LA CÍA. DE TEATRO A LA ADMIN.",
    docField: "companyAdvanceInvoiceUrl", docLabel: "VER FACTURA DEL ANTICIPO",
    dateField: "companyAdvanceInvoiceAt",
    role: "manager", uploadFn: "uploadCompanyAdvanceInvoice", amount: "company_advance",
    uploadLabel: "Subir factura del anticipo", reuploadLabel: "Volver a subir factura",
    requires: "companyAdvanceProofUrl",
    lockedHint: "Se habilita cuando la administración transfiera el anticipo.",
    color: C.company, fill: true,
  },
  {
    key: "company_payment_proof", track: true,
    title: "PAGO DEL SALDO A LA COMPAÑÍA (80%)",
    subtitle: "DESDE LA ADMIN. A LA CÍA. DE TEATRO",
    docField: "companyPaymentProofUrl", docLabel: "BOUCHER PAGO DEL SALDO",
    dateField: "companyPaidAt",
    role: "admin", uploadFn: "uploadCompanyPayment", amount: "company_balance",
    uploadLabel: "Subir comprobante del saldo", reuploadLabel: "Volver a subir comprobante",
    opensAt: "balance_paid",
    lockedHint: "Se habilita cuando el cliente pague el saldo (80%).",
    color: C.company, fill: false,
  },
  {
    key: "company_invoice", track: true,
    title: "FACTURA POR EL SALDO (80%)",
    subtitle: "DESDE LA CÍA. DE TEATRO A LA ADMIN.",
    docField: "companyInvoiceUrl", docLabel: "VER FACTURA DEL SALDO",
    dateField: "companyInvoiceAt",
    role: "manager", uploadFn: "uploadCompanyInvoice", amount: "company_balance",
    uploadLabel: "Subir factura del saldo", reuploadLabel: "Volver a subir factura",
    requires: "companyPaymentProofUrl",
    lockedHint: "Se habilita cuando la administración transfiera el saldo.",
    color: C.company, fill: true,
  },
];

// Paso virtual de la línea de tiempo cuando la reserva fue cancelada.
const CANCELLED_STEP = {
  key: "cancelled", title: "RESERVA CANCELADA", color: C.cancelled, fill: true,
};

export default {
  name: "ReservationDetail",
  components: { ContractEditor },
  props: {
    reservationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      reservation: null,
      play: null,
      selectedKey: "info",
      loading: true,
      sending: false,
      error: "",
      success: "",
      confirmedDate: null,
      uploadFile: null,
      notes: "",
      // { cliente: {...}, cia: {...}, pricing: {...}, annex_html } — los
      // devuelve el backend ya rellenados; cada parte recibe sólo el suyo.
      contracts: {},
      // Pestaña del contrato que mira la administración.
      contractTab: "cliente",
      contractTabs: [
        { kind: "cliente", label: "CONTRATO CLIENTE" },
        { kind: "cia", label: "CONTRATO COMPAÑÍA" },
      ],
      // HTML de cada contrato en el editor (sólo la administración).
      drafts: { cliente: "", cia: "" },
      // Precio de la obra y servicios extra en edición.
      pricingDraft: { function_date: "", play_amount: 0, services: [] },
      // Modal de confirmación del envío de contratos.
      sendConfirmOpen: false,
      sendError: "",
      resendingEmail: false,
      // Modal de confirmación de "restaurar texto original".
      resetConfirmOpen: false,
      resetError: "",
      generatingPdf: false,
      // Aceptación con código enviado al correo de la contraparte.
      codeSent: false,
      sendingCode: false,
      acceptCode: "",
      acceptChecked: false,
      // Error de la aceptación, mostrado dentro del recuadro (el general queda
      // arriba de la página, fuera de la vista).
      acceptError: "",
      // Modal de cancelación (o rechazo del contrato) con motivo.
      cancelOpen: false,
      cancelMode: "cancel",
      cancelReason: "",
      cancelError: "",
    };
  },
  watch: {
    // Al corregir el código se limpia el error anterior.
    acceptCode() {
      this.acceptError = "";
    },
    // Al cambiar de pestaña, el código pedido era para el otro contrato.
    contractTab() {
      this.resetAcceptForm();
    },
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    isAdmin() {
      return this.$store.state.is_admin;
    },
    isManager() {
      return this.isAdmin || this.role === "theater_member";
    },
    isCompanyMember() {
      return this.role === "theater_member";
    },
    isClient() {
      // `Person.role` siempre viene con valor (por defecto "client"), así que no
      // hace falta el "todo lo que no sea gestor es cliente": eso le mostraba
      // acciones de comprador a cualquier rol desconocido.
      return this.role === "client";
    },
    status() {
      return String((this.reservation && this.reservation.status) || "").toLowerCase();
    },
    companyName() {
      return (this.reservation && this.reservation.play && this.reservation.play.company && this.reservation.play.company.name) || "—";
    },
    showDate() {
      const r = this.reservation;
      return (r && (r.confirmedDate || r.requestedDate)) || null;
    },
    // El estreno ya ocurrió si la reserva pasó de ese hito o si llegó la fecha
    // (el backend avanza el estado recién cuando alguien abre la reserva).
    showPremiered() {
      const premiereIdx = STEPS.findIndex((step) => step.key === "balance_pending");
      if (this.statusLevel > premiereIdx && this.status !== "cancelled") return true;
      if (!this.showDate) return false;
      const now = new Date();
      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");
      return String(this.showDate).slice(0, 10) <= today;
    },
    // Estado del flujo principal con el que se dibuja la línea de tiempo: una
    // cancelada muestra hasta donde llegó; los estados legacy del tramo con la
    // compañía cuentan como "saldo pagado".
    effectiveStatus() {
      let s = this.status;
      if (s === "cancelled") {
        s = String((this.reservation && this.reservation.cancelledFromStatus) || "").toLowerCase();
      }
      if (s === "company_invoice_pending" || s === "company_payment_pending") s = "balance_paid";
      return s;
    },
    // Posición del estado actual dentro de STEPS (pending/pending_availability = 0).
    statusLevel() {
      const s = this.effectiveStatus;
      if (s === "pending" || s === "pending_availability") return 0;
      const idx = STEPS.findIndex((step) => step.key === s);
      return idx === -1 ? 0 : idx;
    },
    // Estados alcanzados (0..actual), en orden inverso (el más reciente arriba).
    // Los estados futuros no se muestran; los del tramo con la compañía, sólo
    // a la administración y a la compañía. Una cancelada lleva arriba el aviso.
    states() {
      if (!this.reservation) return [];
      const level = this.statusLevel;
      const cancelled = this.status === "cancelled";
      const out = [];
      for (let idx = 0; idx <= level; idx++) {
        const step = STEPS[idx];
        if (step.managerOnly && !this.isManager) continue;
        const docUrl = step.docField ? this.reservation[step.docField] || null : null;
        out.push({
          ...step,
          state: idx === level && !cancelled ? "active" : "done",
          docUrl,
          lines: this.linesFor(step.key),
          needsAction: idx === level && !cancelled && this.pendingAt(step.key),
        });
      }
      if (cancelled) {
        out.push({ ...CANCELLED_STEP, state: "active", lines: this.linesFor("cancelled"), needsAction: false });
      }
      return out.reverse();
    },
    // Hitos del tramo con la compañía, con su estado: bloqueado, pendiente o
    // con el documento subido.
    trackItems() {
      if (!this.reservation) return [];
      return COMPANY_TRACK.map((step) => {
        const docUrl = this.reservation[step.docField] || null;
        const open = this.trackOpen(step);
        return {
          ...step,
          open,
          docUrl,
          fill: !!docUrl,
          state: docUrl ? "done" : open ? "active" : "locked",
          lines: this.trackLines(step, open, docUrl),
          needsAction: open && !docUrl && this.status !== "cancelled" && this.roleMatches(step.role),
        };
      });
    },
    // El tramo con la compañía aparece para los gestores una vez que el
    // cliente pagó la reserva (20%).
    showCompanyTrack() {
      return this.isManager && !!this.reservation && this.reachedStep("deposit_paid");
    },
    selectedState() {
      if (this.selectedKey === "cancelled") return CANCELLED_STEP;
      return (
        STEPS.find((s) => s.key === this.selectedKey) ||
        COMPANY_TRACK.find((s) => s.key === this.selectedKey) ||
        null
      );
    },
    // Resumen del aviso de cancelación: quién y cuándo.
    cancelledSummary() {
      const r = this.reservation;
      if (!r) return "";
      const roles = { admin: "la administración", theater_member: "la compañía de teatro", client: "el cliente" };
      const by = roles[r.cancelledByRole] || "la administración";
      const name = r.cancelledByRole === "client" && r.cancelledByName ? ` (${r.cancelledByName})` : "";
      const when = r.cancelledAt ? ` el ${this.formatDateTime(r.cancelledAt)}` : "";
      return `Cancelada por ${by}${name}${when}. La fecha volvió a quedar disponible.`;
    },
    // Cancelar con motivo: la administración siempre (salvo ya cancelada); el
    // cliente dueño y la compañía mientras no haya dinero de por medio.
    canCancel() {
      if (!this.reservation || this.status === "cancelled") return false;
      if (this.isAdmin) return true;
      if (!(this.isClient || this.isCompanyMember)) return false;
      return SELF_CANCELLABLE.includes(this.status);
    },
    showFicha() {
      return this.selectedKey === "info" || (this.selectedState && this.selectedState.showsInfo);
    },
    selectedDocUrl() {
      if (!this.selectedState || !this.selectedState.docField || !this.reservation) return null;
      return this.reservation[this.selectedState.docField] || null;
    },
    selectedStateLines() {
      return this.selectedState ? this.linesFor(this.selectedState.key) : [];
    },
    // El botón del documento toma el color del hito, igual que en el mockup:
    // relleno cuando el punto es relleno, contorneado cuando es sólo anillo.
    docButtonStyle() {
      const step = this.selectedState;
      if (!step || !step.color) return {};
      if (step.fill) {
        return { background: step.color, color: "#fff", border: `1px solid ${step.color}` };
      }
      return { background: "#fff", color: "#1a1a1a", border: `2px solid ${step.color}` };
    },
    playFacts() {
      return ((this.play && this.play.facts) || []).filter((f) => f.value);
    },
    hasRegionPrice() {
      return !!(this.play && Number(this.play.priceRegion) > 0);
    },
    // Sólo la administración confirma la fecha, mientras la reserva siga
    // pendiente de confirmación.
    canConfirmDate() {
      if (!(this.isAdmin && this.reservation)) return false;
      return ["pending", "pending_availability", "date_requested"].includes(this.status);
    },
    // ¿Se puede subir el documento del paso seleccionado? En el flujo
    // principal, sólo en el estado actual; en el tramo con la compañía, en
    // cuanto el hito se habilita. Nunca en una reserva cancelada.
    selectedUploadEnabled() {
      const step = this.selectedState;
      if (!step || !step.uploadFn || !this.selectedCanManage || this.status === "cancelled") return false;
      if (step.track) return this.trackOpen(step);
      return this.status === step.key;
    },
    // La administración o la compañía pueden descartar la fecha mientras siga
    // en solicitud.
    canRejectDate() {
      if (!(this.isManager && this.reservation)) return false;
      return ["pending", "date_requested", "date_confirmed"].includes(this.status);
    },
    // Contrato que mira quien está en pantalla: la administración elige la
    // pestaña; la compañía ve el suyo y el cliente el suyo.
    contractKind() {
      if (this.isAdmin) return this.contractTab;
      return this.isCompanyMember ? "cia" : "cliente";
    },
    activeContract() {
      return this.contracts[this.contractKind] || {};
    },
    // Lo acepta su contraparte (con código) o la administración en su nombre,
    // sólo después de enviado y mientras no esté aceptado.
    canAcceptActive() {
      const c = this.activeContract;
      if (this.status !== "contract_pending" || !c.sent_at || c.signed_at) return false;
      if (this.isAdmin) return true;
      return this.contractKind === "cia" ? this.isCompanyMember : this.isClient;
    },
    // Contratos que la administración puede aceptar en nombre de su
    // contraparte: enviados y todavía sin aceptar, en la firma de contratos.
    adminAcceptKinds() {
      if (!this.isAdmin || this.status !== "contract_pending") return [];
      return ["cliente", "cia"].filter((kind) => {
        const c = this.contracts[kind];
        return c && c.sent_at && !c.signed_at;
      });
    },
    // Reenviar el correo del contrato: en la firma de contratos, ya enviado y
    // mientras su contraparte no lo acepte.
    canResendEmail() {
      const c = this.activeContract;
      return this.isAdmin && this.status === "contract_pending" && !!c.sent_at && !c.signed_at;
    },
    // Aviso cuando ya firmó esta parte y falta la otra.
    otherPartyPending() {
      const r = this.reservation;
      if (!r || this.status !== "contract_pending" || !this.activeContract.signed_at) return "";
      if (this.contractKind === "cliente" && !r.companyContractSignedAt) return "Falta la firma de la compañía.";
      if (this.contractKind === "cia" && !r.contractSignedAt) return "Falta la firma del cliente.";
      return "";
    },
    // Datos de quien hizo la reserva: empresa, contacto y correo. Salen de la
    // cuenta del cliente; los de la reserva quedan de respaldo.
    clientInfo() {
      const r = this.reservation;
      if (!r) return null;
      const client = r.client || null;
      return {
        id: client && client.id,
        company: (client && client.company && client.company.name) || "—",
        contact: (client && client.fullName) || r.name || "—",
        email: r.email || (client && client.email) || "",
      };
    },
    signer() {
      return this.activeContract.signer || null;
    },
    pricing() {
      return this.contracts.pricing || null;
    },
    // Temporada de la obra: límites del selector de fecha (ISO "YYYY-MM-DD").
    playSeasonStart() {
      return (this.play && this.play.startDate) || undefined;
    },
    playSeasonEnd() {
      return (this.play && this.play.endDate) || undefined;
    },
    pricingDraftTotal() {
      const d = this.pricingDraft;
      return (Number(d.play_amount) || 0) + d.services.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    },
    // ¿Hay cambios de precios sin guardar?
    pricingDirty() {
      if (!this.pricing) return false;
      return JSON.stringify(this.normalizePricing(this.pricingDraft)) !== JSON.stringify(this.normalizePricing(this.pricing));
    },
    acceptCodeDigits() {
      return String(this.acceptCode || "").replace(/\D/g, "");
    },
    // Sólo la administración edita y envía los contratos, mientras nadie haya
    // aceptado el suyo.
    canEditContracts() {
      return (
        this.isAdmin &&
        this.status === "contract_pending" &&
        !this.reservation.contractSignedAt &&
        !this.reservation.companyContractSignedAt &&
        !!this.contracts.cliente &&
        this.contracts.cliente.html !== undefined
      );
    },
    // El estado seleccionado es un paso con documento subible.
    selectedIsDocStep() {
      return !!(this.selectedState && this.selectedState.uploadFn);
    },
    // ¿Puede el usuario subir/re-subir el documento del paso seleccionado?
    // role "owner"   = cliente o admin (no compañía)
    // role "manager" = admin o compañía
    // role "admin"   = sólo la administración (es quien le paga a la compañía)
    selectedCanManage() {
      const step = this.selectedState;
      return !!step && this.roleMatches(step.role);
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error("No se pudo cargar la sesión", e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.reservation = await fetchReservation(this.reservationId);
        const playId = this.reservation && this.reservation.play && this.reservation.play.id;
        if (playId) {
          try {
            this.play = await fetchPlay(playId);
          } catch (e) {
            console.error("No se pudo cargar el detalle de la obra", e);
          }
        }
        // Los contratos se arman en el backend con los datos de la reserva; si
        // fallan no se cae la vista, sólo queda el hito sin texto.
        await this.loadContracts();
        this.autoSelect();
      } catch (e) {
        this.error = "No se pudo cargar la reserva.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadContracts() {
      try {
        this.setContracts(await fetchContracts(this.reservationId));
      } catch (e) {
        console.error("No se pudieron cargar los contratos", e);
      }
    },
    setContracts(data) {
      // Los precios en edición sin guardar no se pisan al guardar un borrador.
      const keepPricing = this.pricingDirty;
      this.contracts = data || {};
      this.drafts = {
        cliente: (this.contracts.cliente && this.contracts.cliente.html) || "",
        cia: (this.contracts.cia && this.contracts.cia.html) || "",
      };
      if (!keepPricing && this.contracts.pricing) {
        this.pricingDraft = {
          function_date: this.contracts.pricing.function_date || "",
          play_amount: this.contracts.pricing.play_amount,
          services: this.contracts.pricing.services.map((s) => ({ name: s.name, amount: s.amount })),
        };
      }
    },
    normalizePricing(p) {
      return {
        function_date: p.function_date || "",
        play_amount: Number(p.play_amount) || 0,
        services: (p.services || []).map((s) => ({ name: String(s.name || "").trim(), amount: Number(s.amount) || 0 })),
      };
    },
    // Montos: sólo dígitos (ni signo, ni letras, ni decimales). Lo que no sea
    // dígito se descarta al escribir o pegar, y se muestra con puntos de miles.
    setAmount(target, key, event) {
      const digits = String(event.target.value || "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
      target[key] = digits ? Number(digits) : 0;
      // Si el valor no cambió (p. ej. se tecleó una letra), Vue no repinta el
      // campo: se corrige a mano para que la letra no quede a la vista.
      event.target.value = this.formatThousands(target[key]);
    },
    formatThousands(amount) {
      const value = Number(amount) || 0;
      return value ? value.toLocaleString("es-CL") : "";
    },
    addService() {
      this.pricingDraft.services.push({ name: "", amount: 0 });
    },
    removeService(index) {
      this.pricingDraft.services.splice(index, 1);
    },
    async savePricing() {
      await this.runAction(async () => {
        await this.persistPricing();
      }, "Precios guardados.");
    },
    async persistPricing() {
      const dateChanged = this.pricingDraft.function_date !== (this.pricing.function_date || "");
      const data = await saveReservationPricing(this.reservationId, this.normalizePricing(this.pricingDraft));
      // Ya guardados: se toman tal como quedaron en el backend.
      this.contracts = {};
      this.setContracts(data);
      // La fecha se muestra en el encabezado y en la línea de tiempo.
      if (dateChanged) this.reservation = await fetchReservation(this.reservationId);
    },
    async saveContractDraft() {
      const kind = this.contractTab;
      await this.runAction(async () => {
        const other = kind === "cia" ? "cliente" : "cia";
        const otherDraft = this.drafts[other];
        this.setContracts(await saveContract(this.reservationId, kind, this.drafts[kind]));
        // Lo que estaba en el editor del otro contrato no se pierde.
        this.drafts[other] = otherDraft;
      }, "Borrador del contrato guardado.");
    },
    resetContractDraft() {
      this.resetError = "";
      this.resetConfirmOpen = true;
    },
    closeResetConfirm() {
      if (this.sending) return;
      this.resetConfirmOpen = false;
    },
    async confirmResetContract() {
      const kind = this.contractTab;
      this.resetError = "";
      await this.runAction(async () => {
        const other = kind === "cia" ? "cliente" : "cia";
        const otherDraft = this.drafts[other];
        this.setContracts(await saveContract(this.reservationId, kind, "", true));
        this.drafts[other] = otherDraft;
      }, "Se restauró el texto original del contrato.");
      // Si falló, el error se muestra en el modal.
      if (this.error) {
        this.resetError = this.error;
        this.error = "";
        return;
      }
      this.resetConfirmOpen = false;
    },
    // PDF con lo que hay en el editor (sin guardar ni enviar), para revisarlo.
    async downloadActiveContractPdf() {
      const kind = this.contractTab;
      this.generatingPdf = true;
      this.error = "";
      try {
        const blob = await downloadContractPdf(this.reservationId, kind, this.drafts[kind]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = kind === "cia"
          ? `contrato-compania-reserva-${this.reservationId}.pdf`
          : `contrato-reserva-${this.reservationId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        this.error = "No se pudo generar el PDF del contrato.";
        console.error(e);
      } finally {
        this.generatingPdf = false;
      }
    },
    sendAllContracts() {
      const toClient = this.contracts.cliente.recipient;
      const toCompany = this.contracts.cia.recipient;
      if (!toClient || !toCompany) {
        this.error = !toClient
          ? "La reserva no tiene un correo del cliente al que enviar el contrato."
          : "La compañía no tiene un correo de contacto al que enviar su contrato.";
        return;
      }
      this.sendError = "";
      this.sendConfirmOpen = true;
    },
    closeSendConfirm() {
      if (this.sending) return;
      this.sendConfirmOpen = false;
    },
    async confirmSendContracts() {
      const toClient = this.contracts.cliente.recipient;
      const toCompany = this.contracts.cia.recipient;
      this.sendError = "";
      let emailErrors = [];
      await this.runAction(async () => {
        if (this.pricingDirty) await this.persistPricing();
        const data = await sendContracts(this.reservationId, { ...this.drafts });
        emailErrors = (data && data.email_errors) || [];
        await this.loadContracts();
      }, `Contratos enviados a ${toClient} y ${toCompany}.`);
      // Si falló, el error se muestra en el modal, que es donde está mirando.
      if (this.error) {
        this.sendError = this.error;
        this.error = "";
        return;
      }
      this.sendConfirmOpen = false;
      // Los contratos quedaron enviados aunque algún correo fallara: se avisa
      // para reenviarlo desde el botón "Reenviar correo".
      if (emailErrors.length) {
        this.success = "Los contratos quedaron enviados y disponibles en la plataforma.";
        this.error = `${emailErrors.join(" ")} Puedes reenviarlo con «Reenviar correo».`;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    // Seleccionar el estado actual para que su documento/acción sea visible.
    autoSelect() {
      const s = this.status;
      if (s === "cancelled") {
        this.selectedKey = "cancelled";
        return;
      }
      this.selectedKey = STEPS.some((step) => step.key === s) ? this.effectiveStatus : "info";
    },
    // role "owner"   = cliente o admin (no compañía)
    // role "manager" = admin o compañía
    // role "admin"   = sólo la administración
    roleMatches(role) {
      if (role === "owner") return this.isAdmin || this.isClient;
      if (role === "manager") return this.isManager;
      if (role === "admin") return this.isAdmin;
      return false;
    },
    // ¿La reserva llegó (o pasó) por ese estado del flujo principal?
    reachedStep(key) {
      return FLOW_ORDER.indexOf(this.effectiveStatus) >= FLOW_ORDER.indexOf(key);
    },
    // ¿El hito del tramo con la compañía ya está habilitado?
    trackOpen(step) {
      if (!this.reservation) return false;
      if (step.requires) return !!this.reservation[step.requires];
      if (step.opensAt) return this.reachedStep(step.opensAt);
      return true;
    },
    trackLines(step, open, docUrl) {
      if (docUrl) {
        const at = this.reservation[step.dateField];
        return at ? [`SUBIDO EL DÍA ${this.formatDate(at)}`] : ["DOCUMENTO SUBIDO"];
      }
      if (!open) return ["AÚN NO DISPONIBLE"];
      return [step.role === "admin" ? "TRANSFERENCIA PENDIENTE" : "FACTURA PENDIENTE"];
    },
    // Precio de referencia (lista / fuera de la región) al precio de la reserva.
    usePlayPrice(amount) {
      this.pricingDraft.play_amount = Number(amount) || 0;
    },
    openCancel(mode) {
      this.cancelMode = mode;
      this.cancelReason = "";
      this.cancelError = "";
      this.cancelOpen = true;
    },
    closeCancel() {
      if (this.sending) return;
      this.cancelOpen = false;
    },
    async confirmCancel() {
      const reason = this.cancelReason.trim();
      if (!reason) {
        this.cancelError = "Indica el motivo.";
        return;
      }
      this.cancelError = "";
      const message = this.cancelMode === "reject"
        ? "Contrato rechazado: la reserva quedó cancelada."
        : "La reserva quedó cancelada.";
      await this.runAction(() => cancelReservation(this.reservationId, reason), message);
      // Si falló, el error se muestra en el modal, que es donde está mirando.
      if (this.error) {
        this.cancelError = this.error;
        this.error = "";
        return;
      }
      this.cancelOpen = false;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    // ¿Hay algo pendiente por hacer en este estado (para mostrar el badge)?
    pendingAt(key) {
      const r = this.reservation;
      if (!r || this.status === "cancelled") return false;
      if (["pending", "pending_availability", "date_requested"].includes(key)) {
        return true; // falta confirmar la fecha
      }
      if (key === "contract_pending") return !(r.contractSignedAt && r.companyContractSignedAt);
      if (key === "deposit_pending") return !r.depositProofUrl;
      if (key === "deposit_paid") return !r.reservationInvoiceUrl;
      if (key === "balance_invoice_pending") return !r.balanceInvoiceUrl;
      if (key === "show_completed") return !r.balanceProofUrl;
      return false;
    },
    linesFor(key) {
      const r = this.reservation;
      if (!r) return [];
      switch (key) {
        case "pending_availability": {
          const d = (this.play && this.play.startDate) || r.created;
          return d ? [`FIJADA POR LA COMPAÑÍA EL DÍA ${this.formatDate(d)}`] : ["FIJADA POR LA COMPAÑÍA"];
        }
        case "date_requested":
          return [
            r.created ? `REALIZADA EL DÍA ${this.formatDate(r.created)}` : null,
            `HACIA LA COMPAÑÍA ${this.companyName.toUpperCase()}`,
          ].filter(Boolean);
        case "date_confirmed":
          return [
            r.confirmedDate ? `REALIZADA EL DÍA ${this.formatDate(r.confirmedDate)}` : "REALIZADA",
            `DESDE LA COMPAÑÍA ${this.companyName.toUpperCase()}`,
          ];
        case "contract_pending": {
          if (r.contractSignedAt && r.companyContractSignedAt) {
            const last = [r.contractSignedAt, r.companyContractSignedAt].sort().pop();
            return [`REALIZADA EL DÍA ${this.formatDate(last)}`];
          }
          if (!r.contractSentAt) return ["CONTRATOS EN PREPARACIÓN"];
          return [
            `CONTRATOS ENVIADOS EL DÍA ${this.formatDate(r.contractSentAt)}`,
            `CLIENTE: ${r.contractSignedAt ? "FIRMADO" : "FIRMA PENDIENTE"}`,
            `COMPAÑÍA: ${r.companyContractSignedAt ? "FIRMADO" : "FIRMA PENDIENTE"}`,
          ];
        }
        case "deposit_pending": // comprobante del 20%
          if (!r.depositProofUrl) return ["PAGO DEL 20% PENDIENTE"];
          return r.depositPaidAt
            ? [`SUBIDO EL DÍA ${this.formatDate(r.depositPaidAt)}`]
            : ["COMPROBANTE SUBIDO"];
        case "deposit_paid": // factura del 20%
          return r.reservationInvoiceUrl ? ["FACTURA SUBIDA"] : ["FACTURA PENDIENTE"];
        case "balance_invoice_pending": // factura del saldo
          return r.balanceInvoiceUrl ? ["FACTURA SUBIDA"] : ["FACTURA PENDIENTE"];
        case "balance_pending": { // estreno de la función
            const d = this.showDate;
            if (this.showPremiered) {
              return d ? [`ESTRENO REALIZADO EL DÍA ${this.formatDate(d)}`] : ["ESTRENO REALIZADO"];
            }
            return d ? [`FUNCIÓN EL DÍA ${this.formatDate(d)}`] : ["ESPERANDO ESTRENO"];
          }
        case "show_completed": // comprobante del saldo
          return r.balanceProofUrl ? ["COMPROBANTE SUBIDO"] : ["PAGO DEL SALDO PENDIENTE"];
        case "balance_paid":
          return r.balancePaidAt
            ? [`PAGO RECIBIDO EL DÍA ${this.formatDate(r.balancePaidAt)}`]
            : ["PAGO CONFIRMADO"];
        case "company_paid":
          return r.companyInvoiceAt
            ? [`CERRADA EL DÍA ${this.formatDate(r.companyInvoiceAt)}`]
            : ["CERRADA"];
        case "cancelled": {
          const roles = { admin: "LA ADMINISTRACIÓN", theater_member: "LA COMPAÑÍA", client: "EL CLIENTE" };
          const by = roles[r.cancelledByRole] || "LA ADMINISTRACIÓN";
          return [
            r.cancelledAt ? `CANCELADA EL DÍA ${this.formatDate(r.cancelledAt)} POR ${by}` : `CANCELADA POR ${by}`,
            r.cancelReason ? `MOTIVO: ${r.cancelReason}` : null,
          ].filter(Boolean);
        }
        default:
          return [];
      }
    },
    // Punto de la línea de tiempo: relleno o sólo anillo, con el color del paso.
    // Mantiene el resplandor del paso activo, ahora en su propio color.
    dotStyle(step) {
      const color = step.color || C.agreement;
      const style = {
        borderColor: color,
        background: step.fill ? color : "#120c0b",
      };
      if (step.state === "active") {
        style.boxShadow = `0 0 10px ${this.hexToRgba(color, 0.8)}`;
      }
      if (step.state === "locked") {
        style.opacity = 0.45;
      }
      return style;
    },
    hexToRgba(hex, alpha) {
      const value = String(hex || "").replace("#", "");
      const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
      const num = parseInt(full, 16);
      if (Number.isNaN(num)) return `rgba(62, 192, 106, ${alpha})`;
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    select(key) {
      this.selectedKey = key;
    },
    selectInfo() {
      this.selectedKey = "info";
    },
    isPdf(url) {
      return /\.pdf(\?|$)/i.test(String(url || ""));
    },
    isImage(url) {
      return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(url || ""));
    },
    async runAction(action, successMessage = "Acción registrada correctamente.") {
      this.sending = true;
      this.error = "";
      this.success = "";
      const before = this.status;
      try {
        await action();
        // Recargar desde GraphQL (camelCase) y avanzar al siguiente estado.
        this.reservation = await fetchReservation(this.reservationId);
        // Si cambió el estado, los contratos y sus permisos (precios
        // editables, botones de envío…) se calcularon para el estado
        // anterior: se piden de nuevo. Sin esto, al confirmar la fecha la
        // firma de contratos aparecía sin los botones hasta recargar.
        if (this.status !== before) await this.loadContracts();
        this.success = successMessage;
        this.clearFiles();
        this.autoSelect();
      } catch (e) {
        const data = e.response && e.response.data;
        this.error = (data && (data.detail || (Array.isArray(data) && data[0]))) || e.message || "Ocurrió un error.";
        console.error(e);
      } finally {
        this.sending = false;
      }
    },
    clearFiles() {
      this.uploadFile = null;
      this.notes = "";
    },
    async confirmRequestedDate() {
      const date =
        this.reservation.confirmedDate ||
        this.reservation.requestedDate ||
        this.reservation.displayDate;
      if (!date) {
        this.error = "No hay una fecha solicitada para confirmar.";
        return;
      }
      this.confirmedDate = String(date).slice(0, 10);
      if (!this.confirmedDate) return;
      await this.runAction(() => confirmReservationDate(this.reservationId, this.confirmedDate));
    },
    async rejectDate() {
      const ok = window.confirm(
        "¿Rechazar la fecha? La reserva vuelve a quedar a la espera de que se " +
          "acuerde otra fecha. No se cancela."
      );
      if (!ok) return;
      await this.runAction(() => rejectReservationDate(this.reservationId));
    },
    async resendEmail() {
      const kind = this.contractTab;
      const to = this.activeContract.recipient;
      this.resendingEmail = true;
      this.error = "";
      this.success = "";
      try {
        this.setContracts(await resendContractEmail(this.reservationId, kind));
        this.success = `Correo con el contrato reenviado a ${to}.`;
      } catch (e) {
        const data = e.response && e.response.data;
        this.error = (data && (data.detail || (Array.isArray(data) && data[0]))) || "No se pudo reenviar el correo.";
        // El error quedó registrado en el contrato: se recarga para mostrarlo.
        await this.loadContracts();
      } finally {
        this.resendingEmail = false;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    formatDateTime(iso) {
      if (!iso) return "-";
      const dt = new Date(iso);
      if (isNaN(dt.getTime())) return this.formatDate(iso);
      const hh = String(dt.getHours()).padStart(2, "0");
      const mi = String(dt.getMinutes()).padStart(2, "0");
      return `${this.formatDate(iso)} a las ${hh}:${mi}`;
    },
    resetAcceptForm() {
      this.codeSent = false;
      this.acceptCode = "";
      this.acceptChecked = false;
      this.acceptError = "";
    },
    async requestCode() {
      this.sendingCode = true;
      this.acceptError = "";
      try {
        await requestAcceptanceCode(this.reservationId, this.contractKind);
        this.codeSent = true;
      } catch (e) {
        this.acceptError = (e.response && e.response.data && (e.response.data.detail || e.response.data[0])) || "No se pudo enviar el código.";
        console.error(e);
      } finally {
        this.sendingCode = false;
      }
    },
    async acceptActiveContract() {
      this.acceptError = "";
      await this.runAction(async () => {
        await acceptContract(this.reservationId, {
          kind: this.contractKind,
          accepted: this.acceptChecked === true,
          version: this.activeContract.version,
          code: this.acceptCodeDigits,
        });
        await this.loadContracts();
      }, "Contrato aceptado. Te enviamos una copia en PDF por correo.");
      // runAction deja el error arriba de la página; acá se muestra en el
      // recuadro, que es donde está mirando quien acepta.
      if (this.error) {
        this.acceptError = this.error;
        this.error = "";
      }
    },
    async acceptContractAsAdmin(kind) {
      const party = kind === "cia" ? "la compañía" : "el cliente";
      const ok = window.confirm(
        `¿Aceptar el contrato en nombre de ${party}? Quedará registrado que lo aceptó la administración, sin código.`
      );
      if (!ok) return;
      await this.runAction(async () => {
        await acceptContract(this.reservationId, {
          kind,
          accepted: true,
          version: this.contracts[kind].version,
        });
        await this.loadContracts();
      }, `Contrato aceptado en nombre de ${party}.`);
    },
    // Subir o re-subir el documento del paso seleccionado (misma acción: la
    // primera vez avanza el estado, las siguientes solo reemplazan el archivo).
    async doUpload() {
      const step = this.selectedState;
      if (!step || !step.uploadFn || !this.uploadFile) return;
      const fn = UPLOADERS[step.uploadFn];
      await this.runAction(() => fn(this.reservationId, this.uploadFile, this.notes));
    },
    goBack() {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = "/mis-reservas/";
      }
    },
    formatCLP(amount) {
      const value = Number(amount) || 0;
      return "$ " + value.toLocaleString("es-CL");
    },
    formatDate(iso) {
      if (!iso) return "-";
      const onlyDate = String(iso).slice(0, 10);
      const [y, m, d] = onlyDate.split("-");
      if (y && m && d) return `${d}/${m}/${y}`;
      const dt = new Date(iso);
      if (isNaN(dt.getTime())) return iso;
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      return `${dd}/${mm}/${dt.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.rd-panel {
  max-width: 1180px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  padding: 34px 40px 40px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.rd-view {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 40px;
  align-items: start;
}

/* --- Encabezado --------------------------------------------------------- */
.rd-head {
  margin-bottom: 26px;
}

.rd-head-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.5px;
  margin: 0 0 4px;
}

.rd-head-sub {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  margin: 0 0 14px;
}

.rd-info-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rd-info-btn:hover,
.rd-info-btn-active {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.45);
}

.rd-client {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 28px;
  margin: 0 0 6px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.rd-client-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rd-client-label {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
}

.rd-client-value {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  word-break: break-all;
}

.rd-client-link {
  text-decoration: none;
}

.rd-client-link:hover {
  text-decoration: underline;
}

.rd-comment {
  margin: 0 0 6px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.rd-comment-text {
  margin: 4px 0 0;
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}

.rd-client-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  background: #fff;
  color: #1a1a1a;
  border-radius: 6px;
  padding: 8px 14px;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-decoration: none;
}

.rd-client-btn:hover {
  background: #f0f0f0;
}

.rd-confirm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  background: linear-gradient(90deg, #2ea357 0%, #21894a 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rd-confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(46, 163, 87, 0.45);
}

.rd-confirm-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

/* Rechazo de la fecha: mismo formato que confirmar, en rojo. */
.rd-reject-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.rd-reject-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.45);
}
.rd-reject-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

/* Cancelar la reserva: botón contorneado, en rojo. */
.rd-cancel-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0 10px;
  background: transparent;
  color: #ff8a95;
  border: 1px solid rgba(200, 16, 46, 0.7);
  border-radius: 6px;
  padding: 8px 16px;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.rd-cancel-btn:hover:not(:disabled) {
  background: rgba(200, 16, 46, 0.18);
}
.rd-cancel-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Aviso de reserva cancelada. */
.rd-cancelled {
  margin: 0 0 10px;
  padding: 12px 16px;
  border: 1px solid rgba(200, 16, 46, 0.6);
  border-radius: 10px;
  background: rgba(200, 16, 46, 0.14);
}
.rd-cancelled-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ff8a95;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
}
.rd-cancelled-line,
.rd-cancelled-reason {
  margin: 6px 0 0;
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}

/* Motivo de la cancelación (modal). */
.rd-cancel-label {
  display: block;
  margin: 0 0 6px;
  color: #e05a6a;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
}
.rd-cancel-input {
  display: block;
  width: 100%;
  resize: vertical;
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: #fff;
  color: #1a1a1a;
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
}
.rd-cancel-input:focus {
  outline: none;
  border-color: #c8102e;
}

/* Rechazar el contrato desde el recuadro de aceptación. */
.rd-reject-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #cfe9d8;
}
.rd-reject-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  background: transparent;
  border: none;
  padding: 0;
  color: #a30d25;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  cursor: pointer;
}
.rd-reject-link:hover:not(:disabled) {
  text-decoration: underline;
}
.rd-reject-hint {
  color: #777;
  font-size: 11px;
}

/* Tramo administración <-> compañía de teatro, bajo la línea de tiempo. */
.rd-track {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.rd-track-title {
  margin: 0;
  color: #ff1aff;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}
.rd-track-sub {
  margin: 2px 0 12px;
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.7px;
}
.rd-state-locked .rd-state-title,
.rd-state-locked .rd-state-subtitle,
.rd-state-locked .rd-state-line {
  opacity: 0.55;
}

/* --- Estados (timeline) ------------------------------------------------- */
.rd-timeline {
  position: relative;
  padding-left: 6px;
}

.rd-state {
  position: relative;
  display: flex;
  gap: 16px;
  padding: 4px 8px 18px 0;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.rd-state:hover {
  background: rgba(255, 255, 255, 0.03);
}

.rd-state-selected .rd-state-title {
  color: #ff6b6b;
}

.rd-state:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 22px;
  bottom: 0;
  width: 2px;
  background: rgba(255, 255, 255, 0.15);
}

/* El color de cada punto lo aplica dotStyle() según el paso del flujo
   (ver TIMELINE_COLORS en reservationStatus.js). Aquí sólo la forma. */
.rd-state-dot {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 4px;
  border-radius: 50%;
  border: 2px solid #40b04c;
  background: #120c0b;
}

.rd-state-subtitle {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.7px;
  margin-top: 2px;
  opacity: 0.9;
}

.rd-state-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rd-action-badge {
  background: #c8102e;
  color: #fff;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.rd-state-line {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  margin-top: 3px;
}

.rd-state-doc {
  margin-top: 8px;
  background: linear-gradient(90deg, #2ea357 0%, #21894a 100%);
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 6px 14px;
  font-family: var(--font-family-heading);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rd-state-doc:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(46, 163, 87, 0.4);
}

/* --- Documento (derecha) ------------------------------------------------ */
.rd-doc-card {
  background: #f3efe9;
  border-radius: 22px;
  padding: 34px 38px;
  min-height: 420px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
}

.rd-doc-title {
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  margin: 0 0 16px;
}

.rd-doc-desc {
  color: #333;
  font-size: 12px;
  line-height: 1.7;
  text-transform: uppercase;
  margin: 0 0 22px;
}

.rd-facts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rd-fact {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;
}

.rd-fact-label {
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
}

.rd-fact-value {
  color: #444;
  font-size: 11.5px;
  text-transform: uppercase;
  line-height: 1.4;
}

.rd-doc-foot {
  margin-top: 24px;
  color: #888;
  font-size: 10px;
  line-height: 1.5;
}

/* Contrato del sistema (Firma comprador) */
/* Encabezado del contrato, como en el mockup: bajada, obra y fecha. */
.rd-contract-head {
  text-align: center;
  margin-bottom: 20px;
}
.rd-contract-sub {
  margin: 0;
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
}
.rd-contract-heading {
  margin: 4px 0 2px;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}
.rd-contract-date {
  margin: 0;
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.rd-contract-body {
  max-height: 340px;
  overflow-y: auto;
  padding-right: 8px;
  color: #333;
  font-size: 12px;
  line-height: 1.7;
  text-align: justify;
}

.rd-contract-body p {
  margin: 0 0 12px;
}

.rd-contract-actions {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.rd-accept-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: linear-gradient(90deg, #2ea357 0%, #21894a 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 11px 24px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(46, 163, 87, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rd-accept-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(46, 163, 87, 0.45);
}

.rd-accept-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.rd-contract-actions-wrap {
  flex-wrap: wrap;
}

.rd-contract-status {
  margin: 0 0 12px;
  color: #444;
  font-size: 12px;
  line-height: 1.5;
}

.rd-contract-html :deep(h2),
.rd-contract-html :deep(h3) {
  margin: 4px 0 10px;
  color: #1a1a1a;
}

.rd-contract-html :deep(ul),
.rd-contract-html :deep(ol) {
  padding-left: 22px;
  margin-bottom: 12px;
}

.rd-contract-recipient {
  margin: 14px 0 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  color: #333;
  font-size: 12px;
}

.rd-contract-recipient strong {
  color: #1a1a1a;
  word-break: break-all;
}

.rd-contract-recipient-name {
  color: #777;
}

.rd-contract-recipient-missing {
  color: #c8102e;
  font-weight: 700;
}

.rd-accept-box {
  margin-top: 18px;
  padding: 16px 18px;
  border: 1px solid #cfe9d8;
  border-radius: 10px;
  background: #f4fbf6;
}

.rd-accept-title {
  margin: 0 0 8px;
  color: #21894a;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
}

.rd-accept-line {
  margin: 0 0 10px;
  color: #333;
  font-size: 12px;
  line-height: 1.5;
}

.rd-accept-hint {
  margin: -6px 0 10px;
  color: #777;
  font-size: 11px;
}

.rd-code-input {
  max-width: 220px;
}

.rd-secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #bbb;
  border-radius: 6px;
  padding: 10px 16px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-decoration: none;
  cursor: pointer;
}

.rd-secondary-btn:hover {
  background: #f2f2f2;
}

.rd-secondary-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.rd-contract-signed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #21894a;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
}

.rd-contract-waiting {
  color: #8a6d1f;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
}

/* Modal de confirmación del envío de contratos (base: .contact-dialog global). */
.rd-confirm-dialog {
  max-width: 460px;
}

.rd-confirm-text {
  margin: 0 0 16px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  line-height: 1.5;
}

.rd-confirm-list {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.rd-confirm-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.rd-confirm-label {
  color: #e05a6a;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
}

.rd-confirm-value {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.rd-confirm-hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
}

.rd-confirm-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 16px;
  color: #f2c14e;
  font-size: 12px;
}

.rd-confirm-error {
  margin: 0 0 14px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(200, 16, 46, 0.18);
  color: #ff8a95;
  font-size: 12px;
}

.rd-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.rd-confirm-cancel {
  padding: 0 18px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  background: transparent;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  cursor: pointer;
}

.rd-confirm-cancel:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Estado del correo del contrato y botón de reenvío (administración). */
.rd-email-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.rd-email-error,
.rd-email-ok {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1 1 260px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.rd-email-error {
  color: #a30d25;
}

.rd-email-ok {
  color: #2b6b40;
}

/* Precios: obra + servicios extra (antes de enviar los contratos). */
.rd-pricing {
  margin-bottom: 20px;
  padding: 14px 16px;
  border: 1px solid #ddd5ca;
  border-radius: 10px;
  background: #fff;
}

.rd-pricing-title {
  margin: 0 0 10px;
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
}

/* Precios de lista de la obra (referencia) y comentario del cliente. */
.rd-pricing-ref {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  margin: 0 0 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #faf8f5;
  color: #444;
  font-size: 12px;
}
.rd-pricing-ref strong {
  color: #1a1a1a;
}
.rd-pricing-ref-muted {
  color: #888;
}
.rd-pricing-use {
  margin-left: 6px;
  padding: 2px 8px;
  border: 1px solid #bbb;
  border-radius: 4px;
  background: #fff;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
  cursor: pointer;
}
.rd-pricing-use:hover:not(:disabled) {
  background: #f2f2f2;
}
.rd-pricing-comment {
  margin: 0 0 12px;
  color: #444;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}

.rd-pricing-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  color: #222;
  font-size: 13px;
}

.rd-pricing-name {
  flex: 1;
  min-width: 0;
}

.rd-pricing-fixed {
  font-weight: 600;
}

.rd-pricing-amount {
  width: 150px;
  text-align: right;
}

.rd-pricing-input.rd-pricing-date {
  width: 160px;
  text-align: left;
}

.rd-pricing-input {
  padding: 7px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  color: #1a1a1a;
  font-size: 13px;
}

.rd-pricing-input:disabled {
  background: #f4f4f4;
}

.rd-pricing-remove,
.rd-pricing-spacer {
  width: 32px;
  flex: 0 0 32px;
}

.rd-pricing-remove {
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #c8102e;
  cursor: pointer;
}

.rd-pricing-remove:hover:not(:disabled) {
  background: #fbe9ec;
}

.rd-pricing-add {
  margin: 2px 0 10px;
}

.rd-pricing-total {
  padding-top: 8px;
  border-top: 1px solid #e3ddd4;
  font-weight: 700;
}

.rd-pricing-hint {
  margin: 0 0 10px;
  color: #666;
  font-size: 12px;
}

/* Pestañas de los dos contratos (administración). */
.rd-contract-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  border-bottom: 1px solid #ddd5ca;
}

.rd-contract-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #666;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  cursor: pointer;
}

.rd-contract-tab-active {
  border-bottom-color: #c8102e;
  color: #1a1a1a;
}

/* Vista previa del anexo de precios, que no se edita a mano. */
.rd-contract-annex {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px dashed #c9bfb2;
  border-radius: 8px;
  background: #faf8f5;
  max-height: none;
}

.rd-contract-annex-note {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #777 !important;
  font-size: 11px;
}

.rd-contract-html :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.rd-contract-html :deep(td),
.rd-contract-html :deep(th) {
  padding: 4px 8px;
  border: 1px solid #d6d0c7;
}

@media (max-width: 600px) {
  .rd-pricing-amount {
    width: 110px;
  }
}

.rd-doc-viewer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}

.rd-doc-frame {
  width: 100%;
  height: 420px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fff;
}

.rd-doc-img {
  max-width: 100%;
  max-height: 460px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rd-doc-open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  text-decoration: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
}

.rd-doc-info-line {
  color: #444;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 6px;
}

.rd-doc-empty {
  color: #999;
  font-size: 12px;
  margin-top: 10px;
}

.rd-action {
  margin-top: 22px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 18px;
}

.rd-action-title {
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.5px;
  margin: 0 0 14px;
}

/* Texto del formulario legible sobre la tarjeta clara. */
.rd-action p {
  color: #333;
}

/* Forzar los campos de Vuetify (acciones y re-subida) a fondo blanco con texto
   oscuro; si no, se ven "transparentes" sobre la tarjeta crema. */
.rd-doc-card :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.rd-doc-card :deep(.v-field__input),
.rd-doc-card :deep(.v-field input),
.rd-doc-card :deep(.v-field textarea) {
  color: #1a1a1a !important;
}

.rd-doc-card :deep(.v-field-label) {
  color: #6a6a6a !important;
  opacity: 1;
}

.rd-doc-card :deep(.v-field__outline) {
  color: rgba(0, 0, 0, 0.25) !important;
  --v-field-border-opacity: 1;
}

.rd-doc-card :deep(.v-icon),
.rd-doc-card :deep(.v-field__prepend-inner .v-icon),
.rd-doc-card :deep(.v-messages) {
  color: #6a6a6a !important;
  opacity: 1;
}

.rd-reupload {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  width: 100%;
}

/* --- Volver ------------------------------------------------------------- */
.rd-volver-row {
  display: flex;
  justify-content: center;
  margin-top: 34px;
}

.rd-volver {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.rd-volver:hover {
  opacity: 0.72;
}

@media (max-width: 900px) {
  .rd-view {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}
</style>
